/**
 * Portal Customer Detail (360 View) Page.
 *
 * Portal-themed wrapper around the Customer 360 data.
 * Reuses the same API service (customerService) but with portal-specific
 * layout, breadcrumbs, and back navigation to /portal/customers.
 *
 * Route: /portal/customers/:slug
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDate, formatDateTime, formatFileSize } from '../../utils/dateFormatters';
import { formatMoney, totalOwed } from '../../utils/formatting';
import { formatTierLadder } from '../../utils/tierPricing';
import { customerService } from '../../services/customers';
import { bolReviewService } from '../../services/bolReview';
import OrderDetailModal from '../../components/portal/OrderDetailModal';
import CreateOrderDialog from '../../components/portal/CreateOrderDialog';
import type { Customer360Profile, CustomerQuoteItem, CustomerSalesContactItem, CustomerShipLocationItem, CustomerSpecialPricingProductItem } from '../../types/customer';
import {
  CUSTOMER_STATUS_LABELS as STATUS_LABELS,
  TIMELINE_EVENT_COLORS as EVENT_COLORS,
  TIMELINE_EVENT_LABELS as EVENT_LABELS,
  TIMELINE_SUPER_TYPE_LABELS as SUPER_LABELS,
  TIMELINE_SUPER_TYPE_COLORS as SUPER_COLORS,
  TIMELINE_SUB_TYPE_LABELS as SUB_LABELS,
  TIMELINE_EVENT_TO_SUPER_TYPE as EVENT_TO_SUPER,
} from '../../types/customer';
import { trackEvent } from '../../services/portalTracking';
import RecentOrdersCard from './customerDetail/RecentOrdersCard';
import { buildCreateOrderPrefill, createdOrderUrl } from './customerDetail/createOrderFromQuote';
import CustomerComplaintsTab from '../../components/portal/complaints/CustomerComplaintsTab';
import portalService from '../../services/portal';
import OrderTimelineTab from '../../components/portal/OrderTimelineTab';
import DocumentsPanel from '../../components/portal/DocumentsPanel';
import type { OrderDocumentItem } from '../../types/portal';
import styles from './PortalCustomerDetailPage.module.css';

const STATUS_COLORS: Record<string, string> = {
  A: '#16a34a',
  I: '#dc2626',
  T: '#f59e0b',
};

const CATEGORY_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6b7280',
];

const TAB_KEYS = ['overview', 'timeline', 'orders', 'quotes', 'emails', 'locations', 'documents', 'complaints', 'crm', 'financials'] as const;
type TabKey = typeof TAB_KEYS[number];

// =============================================================================
// Main Export
// =============================================================================

export default function PortalCustomerDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Customer360Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  // "Create Order from Quote" (Quotes tab): the quote the dialog opens for.
  const [createFromQuote, setCreateFromQuote] = useState<CustomerQuoteItem | null>(null);
  const createOrderPrefill = useMemo(
    () => (profile && createFromQuote ? buildCreateOrderPrefill(profile, createFromQuote) : null),
    [profile, createFromQuote],
  );

  const handleCreateOrderFromQuote = useCallback((quote: CustomerQuoteItem) => {
    if (!profile) return;
    trackEvent('action', 'quote_create_order_open', {
      customer_no: profile.contact_info.sage_customer_no,
      quote_id: quote.id,
      quote_source_id: quote.source_id,
      quote_status: quote.status,
    });
    setCreateFromQuote(quote);
  }, [profile]);

  const handleOrderCreatedFromQuote = useCallback((orderId: string) => {
    trackEvent('action', 'order_created', {
      order_id: orderId,
      source: 'quote',
      quote_id: createFromQuote?.id ?? null,
      quote_source_id: createFromQuote?.source_id ?? null,
    });
    setCreateFromQuote(null);
    // The draft is edited where every other new draft is: the orders page
    // opens the order modal from this deep link.
    navigate(createdOrderUrl(orderId));
  }, [createFromQuote, navigate]);

  const fetchProfile = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const data = await customerService.getProfile(slug);
      setProfile(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 404 ? 'Customer not found' : 'Failed to load customer profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchProfile();
    if (slug) {
      trackEvent('view', 'customer_detail', { slug });
    }
  }, [fetchProfile, slug]);

  if (loading) {
    return <div className={styles.loading}>Loading customer profile...</div>;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorText}>{error}</div>
        <button className={styles.backBtn} onClick={() => navigate('/portal/customers')}>
          &larr; Back to Customers
        </button>
      </div>
    );
  }

  if (!profile) return null;

  const { contact_info, salesperson, sales_metrics, ar_aging } = profile;

  return (
    <div className={styles.page}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <button className={styles.breadcrumbLink} onClick={() => navigate('/portal/customers')}>
          Customers
        </button>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>{contact_info.customer_name || slug}</span>
      </nav>

      {/* Profile Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.customerName}>{contact_info.customer_name}</h1>
          <div className={styles.headerMeta}>
            <span className={styles.metaItem}>{contact_info.sage_customer_no}</span>
            {contact_info.city && contact_info.state && (
              <span className={styles.metaItem}>{contact_info.city}, {contact_info.state}</span>
            )}
            {salesperson?.salesperson_name && (
              <span className={styles.metaItem} title="Sage ERP assigned salesperson (independent rep of record)">Sales Rep: {salesperson.salesperson_name}</span>
            )}
            {salesperson?.regional_responsible_name && (
              <span
                className={styles.metaItem}
                title="Regional responsible: the in-house ChemMasters salesperson this rep reports to"
                data-testid="customer-header-regional-responsible"
              >
                Regional Resp.: {salesperson.regional_responsible_name}
              </span>
            )}
          </div>
        </div>
        <div className={styles.headerRight}>
          <span
            className={styles.statusBadge}
            style={{ backgroundColor: STATUS_COLORS[contact_info.customer_status || ''] || '#6b7280' }}
          >
            {STATUS_LABELS[contact_info.customer_status || ''] || contact_info.customer_status || 'Unknown'}
          </span>
          {contact_info.credit_hold === 'Y' && (
            <span className={styles.statusBadge} style={{ backgroundColor: '#dc2626' }}>
              Credit Hold
            </span>
          )}
        </div>
      </div>

      {/* KPI Row */}
      <div className={styles.kpiRow}>
        <KpiCard label="YTD Sales" value={formatMoney(sales_metrics.ytd_sales)} change={sales_metrics.ytd_vs_lytd_pct} />
        <KpiCard label="LYTD Sales" value={formatMoney(sales_metrics.lytd_sales)} />
        <KpiCard label="LY Total" value={formatMoney(sales_metrics.ly_total_sales)} />
        <KpiCard label="Total Balance" value={formatMoney(totalOwed(ar_aging))} />
        <KpiCard label="Last Order" value={sales_metrics.last_order_date ? formatDate(sales_metrics.last_order_date) : 'N/A'} />
        <KpiCard label="Credit Limit" value={formatMoney(contact_info.credit_limit)} />
      </div>

      {/* Tab Bar */}
      <div className={styles.tabBar}>
        {TAB_KEYS.map((key) => (
          <button
            key={key}
            className={`${styles.tab} ${activeTab === key ? styles.tabActive : ''}`}
            onClick={() => {
              trackEvent('click', 'customer_detail_tab', { tab: key });
              if (key === 'complaints') {
                trackEvent('click', 'customer_complaints_tab', { customer_no: contact_info.sage_customer_no });
              }
              setActiveTab(key);
            }}
            type="button"
            value={key}
            data-action="customer_detail_tab"
            data-record-value="tab"
            aria-pressed={activeTab === key}
          >
            {tabLabel(key, profile)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === 'overview' && (
          <OverviewTab profile={profile} onViewAllOrders={() => setActiveTab('orders')} />
        )}
        {activeTab === 'timeline' && <TimelineTab profile={profile} />}
        {activeTab === 'orders' && <OrdersTab profile={profile} />}
        {activeTab === 'quotes' && (
          <QuotesTab profile={profile} onCreateOrder={handleCreateOrderFromQuote} />
        )}
        {activeTab === 'emails' && <EmailsTab profile={profile} />}
        {activeTab === 'locations' && <LocationsTab profile={profile} />}
        {activeTab === 'documents' && (
          <CustomerDocumentsTab
            customerNo={contact_info.sage_customer_no || ''}
          />
        )}
        {activeTab === 'complaints' && (
          <CustomerComplaintsTab
            customerNo={contact_info.sage_customer_no || ''}
            customerName={contact_info.customer_name ?? null}
          />
        )}
        {activeTab === 'crm' && <CrmTab profile={profile} />}
        {activeTab === 'financials' && <FinancialsTab profile={profile} />}
      </div>

      {/* Create Order from Quote (Quotes tab) */}
      <CreateOrderDialog
        isOpen={createOrderPrefill !== null}
        prefill={createOrderPrefill}
        onClose={() => setCreateFromQuote(null)}
        onOrderCreated={handleOrderCreatedFromQuote}
      />
    </div>
  );
}

function tabLabel(key: TabKey, profile: Customer360Profile): string {
  switch (key) {
    case 'overview': return 'Overview';
    case 'timeline': return 'Timeline';
    case 'orders': return `Orders (${profile.orders_total}${profile.bols_total ? ` · ${profile.bols_total} BOL` : ''})`;
    case 'quotes': return `Quotes (${profile.quotes_total})`;
    case 'emails': return `Emails (${profile.emails_total})`;
    case 'locations': {
      const locCount = profile.ship_locations.length + profile.sage_ship_to_addresses.length;
      const contactCount = profile.sales_contacts.length;
      return `Contacts & Locations (${contactCount + locCount})`;
    }
    case 'documents': return 'Documents';
    case 'complaints': return 'Complaints';
    case 'crm': return `CRM (${profile.notes.length + profile.account_updates.length + (profile.sage_order_notes ? 1 : 0)})`;
    case 'financials': return 'Financials';
  }
}

// =============================================================================
// Documents Tab — customer rollup
// =============================================================================

function CustomerDocumentsTab({ customerNo }: { customerNo: string }) {
  // `loading` is derived rather than toggled inside the effect (eslint
  // react-hooks/set-state-in-effect): each (customerNo, tick) pair is a fetch
  // key, the resolved list is stamped with the key it answers, and the tab is
  // loading whenever the stamped key is not the current one. A refetch keeps
  // the previous list on screen until the new one lands.
  const [fetched, setFetched] = useState<{ key: string; documents: OrderDocumentItem[] } | null>(null);
  const [tick, setTick] = useState(0);
  const fetchKey = `${customerNo}|${tick}`;
  const loading = !!customerNo && fetched?.key !== fetchKey;

  useEffect(() => {
    if (!customerNo) return;
    let cancelled = false;
    portalService
      .getCustomerDocuments(customerNo)
      .then((res) => {
        if (!cancelled) setFetched({ key: fetchKey, documents: res.documents });
      })
      .catch(() => {
        if (!cancelled) setFetched({ key: fetchKey, documents: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [customerNo, fetchKey]);

  if (!customerNo) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
        No Sage customer number on file — documents tab is read-only.
      </div>
    );
  }

  return (
    <DocumentsPanel
      scope={{ kind: 'customer', customerNo }}
      documents={fetched?.documents ?? []}
      loading={loading}
      onRefetch={() => setTick((t) => t + 1)}
    />
  );
}

// =============================================================================
// KPI Card
// =============================================================================

function KpiCard({ label, value, change }: { label: string; value: string; change?: number | null }) {
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiValue}>{value}</div>
      {change != null && (
        <div className={`${styles.kpiChange} ${change >= 0 ? styles.salesUp : styles.salesDown}`}>
          {change >= 0 ? '+' : ''}{change.toFixed(1)}% vs LYTD
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Overview Tab
// =============================================================================

function OverviewTab({ profile, onViewAllOrders }: { profile: Customer360Profile; onViewAllOrders: () => void }) {
  const { contact_info, salesperson, enrichment, category_breakdown } = profile;
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  return (
    <div className={styles.twoCol}>
      <div>
        <div className={styles.card}>
          <div className={styles.cardTitle}>Contact Information</div>
          <div className={styles.dataGrid}>
            <DataField label="Phone" value={contact_info.telephone_no} />
            <DataField label="Billing Email" value={contact_info.email_address} />
            <DataField label="Fax" value={contact_info.fax_no} />
            <DataField label="Billing Address" value={[contact_info.address_line1, contact_info.address_line2].filter(Boolean).join(', ')} />
            <DataField label="City / State" value={[contact_info.city, contact_info.state, contact_info.zip_code].filter(Boolean).join(', ')} />
            {contact_info.cp_email_list_shipping && (
              <DataField label="Order Emails" value={contact_info.cp_email_list_shipping} />
            )}
            <DataField label="Terms" value={contact_info.terms_code} />
            <DataField label="Price Level" value={contact_info.price_level} />
            <DataField label="Customer Since" value={formatDate(contact_info.date_created)} />
          </div>
        </div>

        {salesperson && (
          <div className={styles.card}>
            <div className={styles.cardTitle}>Assigned Sales Rep (Sage)</div>
            <div className={styles.dataGrid} data-testid="customer-sage-rep-card">
              <DataField label="Name" value={salesperson.salesperson_name} />
              <DataField label="Sage No." value={salesperson.salesperson_no} />
              <DataField label="Email" value={salesperson.email_address} />
              <DataField label="Phone" value={salesperson.telephone_no} />
              {/* The in-house rep behind an outside agency code, from the same
                  regional_rep_mappings source as the customers list's
                  "Regional Resp." column (kanban 0a59332f). */}
              <DataField
                label="Regional Responsible"
                value={salesperson.regional_responsible_name || 'None (no in-house rep mapped)'}
              />
            </div>
          </div>
        )}

        {enrichment && (
          <div className={styles.card}>
            <div className={styles.cardTitle}>Enrichment</div>
            <div className={styles.dataGrid}>
              <DataField label="Industry" value={enrichment.industry_type} />
              <DataField label="Customer Type" value={enrichment.customer_type} />
              <DataField label="Primary Contact" value={enrichment.primary_contact_name} />
              <DataField label="Contact Phone" value={enrichment.primary_contact_phone} />
              <DataField label="Contact Email" value={enrichment.primary_contact_email} />
              <DataField label="Company Group" value={enrichment.company_group} />
              <DataField label="Business" value={enrichment.udf_business} />
            </div>
          </div>
        )}
      </div>

      <div>
        <RecentOrdersCard
          orders={profile.orders}
          ordersTotal={profile.orders_total}
          onOpenOrder={setSelectedOrderId}
          onViewAll={onViewAllOrders}
        />

        <div className={styles.card}>
          <div className={styles.cardTitle}>Sales by Category (YTD)</div>
          {category_breakdown.length === 0 ? (
            <div className={styles.emptyState}>No sales data for current year</div>
          ) : (
            category_breakdown.map((cat, i) => (
              <div key={cat.category} className={styles.categoryBar}>
                <span className={styles.categoryName}>{cat.category}</span>
                <div className={styles.categoryBarTrack}>
                  <div
                    className={styles.categoryBarFill}
                    style={{
                      width: `${cat.percentage}%`,
                      backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                    }}
                  />
                </div>
                <span className={styles.categoryPct}>{cat.percentage}%</span>
                <span className={styles.categoryAmount}>{formatMoney(cat.amount)}</span>
              </div>
            ))
          )}
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>Recent Activity</div>
          {profile.timeline.length === 0 ? (
            <div className={styles.emptyState}>No recent activity</div>
          ) : (
            <div className={styles.timeline}>
              {profile.timeline.slice(0, 8).map((item, i) => (
                <div key={i} className={styles.timelineItem}>
                  <div
                    className={styles.timelineDot}
                    style={{ backgroundColor: EVENT_COLORS[item.event_type] || '#6b7280' }}
                  />
                  <div className={styles.timelineTitle}>
                    <span
                      className={styles.timelineTag}
                      style={{ backgroundColor: EVENT_COLORS[item.event_type] || '#6b7280' }}
                    >
                      {EVENT_LABELS[item.event_type] || item.event_type}
                    </span>
                    {item.title}
                  </div>
                  {item.description && <div className={styles.timelineDesc}>{item.description}</div>}
                  <div className={styles.timelineDate}>{formatDateTime(item.timestamp)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <OrderDetailModal
        isOpen={!!selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        orderId={selectedOrderId}
        onOrderAction={() => {/* no-op: read-only context */}}
      />
    </div>
  );
}

// =============================================================================
// Timeline Tab
// =============================================================================

function TimelineTab({ profile }: { profile: Customer360Profile }) {
  // Filter state: 'all' | super_type key | specific event_type
  const [superFilter, setSuperFilter] = useState<string>('all');
  const [subFilter, setSubFilter] = useState<string | null>(null);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Build super-type counts from timeline data
  const superCounts: Record<string, number> = {};
  const subCounts: Record<string, Record<string, number>> = {};
  for (const item of profile.timeline) {
    const st = item.super_type || EVENT_TO_SUPER[item.event_type] || 'other';
    superCounts[st] = (superCounts[st] || 0) + 1;
    if (!subCounts[st]) subCounts[st] = {};
    subCounts[st][item.event_type] = (subCounts[st][item.event_type] || 0) + 1;
  }

  // Ordered super-types: only show those present in the data
  const superOrder = ['email', 'order', 'quote', 'invoice', 'call', 'visit', 'meeting', 'other'];
  const activeSuperTypes = superOrder.filter((st) => superCounts[st]);

  // Has sub-types? (groups with >1 distinct event_type)
  function hasSubTypes(st: string): boolean {
    return SUB_LABELS[st] != null && Object.keys(subCounts[st] || {}).length > 1;
  }

  // Filter logic
  const filtered = profile.timeline.filter((item) => {
    if (superFilter === 'all') return true;
    const itemSuper = item.super_type || EVENT_TO_SUPER[item.event_type] || 'other';
    if (itemSuper !== superFilter) return false;
    if (subFilter) return item.event_type === subFilter;
    return true;
  });

  function handleSuperClick(st: string) {
    if (superFilter === st && !subFilter) {
      // Already selected with no sub-filter — toggle off to 'all'
      setSuperFilter('all');
      setSubFilter(null);
      setOpenDropdown(null);
    } else {
      setSuperFilter(st);
      setSubFilter(null);
      // Toggle dropdown for groups with sub-types
      if (hasSubTypes(st)) {
        setOpenDropdown(openDropdown === st ? null : st);
      } else {
        setOpenDropdown(null);
      }
    }
    setExpandedIdx(null);
  }

  function handleSubClick(st: string, eventType: string | null) {
    setSuperFilter(st);
    setSubFilter(eventType && subFilter !== eventType ? eventType : null);
    setOpenDropdown(null);
    setExpandedIdx(null);
  }

  function isTripReport(meta: Record<string, unknown> | null | undefined): boolean {
    return meta?.source === 'trip_report';
  }

  return (
    <div className={styles.card}>
      {/* Super-type filter row */}
      <div className={styles.filterRow}>
        <button
          className={`${styles.filterBtn} ${superFilter === 'all' ? styles.filterBtnActive : ''}`}
          onClick={() => { setSuperFilter('all'); setSubFilter(null); setOpenDropdown(null); setExpandedIdx(null); }}
        >
          All ({profile.timeline.length})
        </button>
        {activeSuperTypes.map((st) => {
          const isActive = superFilter === st;
          const color = SUPER_COLORS[st] || '#6b7280';
          return (
            <div key={st} className={styles.filterGroup}>
              <button
                className={`${styles.filterBtn} ${isActive ? styles.filterBtnActive : ''}`}
                style={isActive ? { backgroundColor: color, borderColor: color } : undefined}
                onClick={() => handleSuperClick(st)}
              >
                {SUPER_LABELS[st] || st} ({superCounts[st]})
                {hasSubTypes(st) && (
                  <span className={styles.filterDropdownArrow}>{openDropdown === st ? '\u25B2' : '\u25BC'}</span>
                )}
              </button>
              {/* Sub-type dropdown */}
              {openDropdown === st && hasSubTypes(st) && (
                <div className={styles.filterDropdown}>
                  <button
                    className={`${styles.filterDropdownItem} ${isActive && !subFilter ? styles.filterDropdownItemActive : ''}`}
                    onClick={() => handleSubClick(st, null)}
                  >
                    All {SUPER_LABELS[st]} ({superCounts[st]})
                  </button>
                  {Object.entries(subCounts[st] || {}).map(([eventType, count]) => (
                    <button
                      key={eventType}
                      className={`${styles.filterDropdownItem} ${subFilter === eventType ? styles.filterDropdownItemActive : ''}`}
                      onClick={() => handleSubClick(st, eventType)}
                    >
                      <span
                        className={styles.filterDropdownDot}
                        style={{ backgroundColor: EVENT_COLORS[eventType] || color }}
                      />
                      {(SUB_LABELS[st] && SUB_LABELS[st][eventType]) || EVENT_LABELS[eventType] || eventType} ({count})
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Active sub-filter indicator */}
      {subFilter && (
        <div className={styles.subFilterIndicator}>
          Showing: {(SUB_LABELS[superFilter] && SUB_LABELS[superFilter][subFilter]) || EVENT_LABELS[subFilter] || subFilter}
          <button
            className={styles.subFilterClear}
            onClick={() => { setSubFilter(null); setExpandedIdx(null); }}
          >
            &times;
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className={styles.emptyState}>No events to display</div>
      ) : (
        <div className={styles.timeline}>
          {filtered.map((item, i) => {
            const isTrip = isTripReport(item.metadata);
            const isExpanded = expandedIdx === i;

            return (
              <div
                key={i}
                className={`${styles.timelineItem} ${isTrip ? styles.timelineItemClickable : ''} ${isExpanded ? styles.timelineItemExpanded : ''}`}
                onClick={isTrip ? () => setExpandedIdx(isExpanded ? null : i) : undefined}
                role={isTrip ? 'button' : undefined}
                tabIndex={isTrip ? 0 : undefined}
                onKeyDown={isTrip ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedIdx(isExpanded ? null : i); } } : undefined}
              >
                <div
                  className={styles.timelineDot}
                  style={{ backgroundColor: EVENT_COLORS[item.event_type] || '#6b7280' }}
                />
                <div className={styles.timelineTitle}>
                  <span
                    className={styles.timelineTag}
                    style={{ backgroundColor: EVENT_COLORS[item.event_type] || '#6b7280' }}
                  >
                    {EVENT_LABELS[item.event_type] || item.event_type}
                  </span>
                  {item.title}
                  {isTrip && (
                    <span className={styles.tripBadge}>Trip Report</span>
                  )}
                  {isTrip && (
                    <span className={styles.expandArrow}>{isExpanded ? '\u25B2' : '\u25BC'}</span>
                  )}
                </div>
                {item.description && <div className={styles.timelineDesc}>{item.description}</div>}
                {!isExpanded && item.metadata?.sentiment != null && (
                  <div className={styles.timelineDesc} style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                    Sentiment: {String(item.metadata.sentiment)}
                    {item.metadata.city != null ? ` \u00B7 ${String(item.metadata.city)}` : ''}
                    {item.metadata.state != null ? `, ${String(item.metadata.state)}` : ''}
                  </div>
                )}
                {isExpanded && item.metadata && (
                  <TripReportDetail metadata={item.metadata} />
                )}
                <div className={styles.timelineDate}>{formatDateTime(item.timestamp)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Trip Report Detail (expanded view inside timeline)
// =============================================================================

const SENTIMENT_STYLES: Record<string, { bg: string; fg: string; label: string }> = {
  positive: { bg: '#dcfce7', fg: '#16a34a', label: 'Positive' },
  negative: { bg: '#fee2e2', fg: '#dc2626', label: 'Negative' },
  neutral: { bg: '#f3f4f6', fg: '#6b7280', label: 'Neutral' },
  mixed: { bg: '#fef3c7', fg: '#92400e', label: 'Mixed' },
};

function TripReportDetail({ metadata }: { metadata: Record<string, unknown> }) {
  const fullSummary = metadata.full_summary as string | undefined;
  const sentiment = metadata.sentiment as string | undefined;
  const salesperson = metadata.salesperson as string | undefined;
  const city = metadata.city as string | undefined;
  const state = metadata.state as string | undefined;
  const contacts = metadata.contacts as string[] | undefined;
  const products = metadata.products as string[] | undefined;
  const actionItems = metadata.action_items as string[] | undefined;

  const sentimentStyle = sentiment ? SENTIMENT_STYLES[sentiment.toLowerCase()] : null;
  const location = [city, state].filter(Boolean).join(', ');

  return (
    <div className={styles.tripDetail} onClick={(e) => e.stopPropagation()}>
      {/* Full summary */}
      {fullSummary && (
        <div className={styles.tripSection}>
          <div className={styles.tripSectionLabel}>Full Summary</div>
          <div className={styles.tripSummaryText}>{fullSummary}</div>
        </div>
      )}

      {/* Meta row: sentiment, salesperson, location */}
      <div className={styles.tripMetaRow}>
        {sentimentStyle && (
          <span
            className={styles.tripPill}
            style={{ backgroundColor: sentimentStyle.bg, color: sentimentStyle.fg }}
          >
            {sentimentStyle.label}
          </span>
        )}
        {salesperson && (
          <span className={styles.tripMeta}>Rep: {salesperson}</span>
        )}
        {location && (
          <span className={styles.tripMeta}>{location}</span>
        )}
      </div>

      {/* Contacts mentioned */}
      {contacts && contacts.length > 0 && (
        <div className={styles.tripSection}>
          <div className={styles.tripSectionLabel}>Contacts Mentioned</div>
          <div className={styles.tripChips}>
            {contacts.map((c, i) => (
              <span key={i} className={styles.tripChip}>{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Products mentioned */}
      {products && products.length > 0 && (
        <div className={styles.tripSection}>
          <div className={styles.tripSectionLabel}>Products Mentioned</div>
          <div className={styles.tripChips}>
            {products.map((p, i) => (
              <span key={i} className={`${styles.tripChip} ${styles.tripChipProduct}`}>{p}</span>
            ))}
          </div>
        </div>
      )}

      {/* Action items */}
      {actionItems && actionItems.length > 0 && (
        <div className={styles.tripSection}>
          <div className={styles.tripSectionLabel}>Action Items</div>
          <ul className={styles.tripActionList}>
            {actionItems.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Orders Tab
// =============================================================================

function OrdersTab({ profile }: { profile: Customer360Profile }) {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [expandedTimeline, setExpandedTimeline] = useState<string | null>(null);
  const [docCounts, setDocCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (profile.orders.length === 0) return;
    const orderIds = profile.orders.map((o) => o.id);
    portalService
      .getDocumentCounts(orderIds)
      .then((res) => setDocCounts(res.counts))
      .catch(() => setDocCounts({}));
  }, [profile.orders]);

  const handleTimelineToggle = (e: React.MouseEvent, orderId: string) => {
    e.stopPropagation();
    setExpandedTimeline(expandedTimeline === orderId ? null : orderId);
  };

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardTitle}>Orders ({profile.orders_total})</div>
        {profile.orders.length === 0 ? (
          <div className={styles.emptyState}>No orders found</div>
        ) : (
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Status</th>
                <th>Source</th>
                <th>Sage #</th>
                <th>PO #</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Items</th>
                <th style={{ textAlign: 'center' }}>Docs</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {profile.orders.map((o) => (
                <React.Fragment key={o.id}>
                  <tr
                    className={styles.clickableRow}
                    onClick={() => setSelectedOrderId(o.id)}
                    title="View order details"
                  >
                    <td>
                      <button
                        className={styles.timelineExpandBtn}
                        onClick={(e) => handleTimelineToggle(e, o.id)}
                        title={expandedTimeline === o.id ? 'Hide timeline' : 'Show timeline'}
                      >
                        {expandedTimeline === o.id ? '▼' : '▶'}
                      </button>
                    </td>
                    <td>
                      <span className={`${styles.orderBadge} ${styles[`order_${o.status}`] || ''}`}>
                        {o.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>{o.source_type.replace(/_/g, ' ')}</td>
                    <td className={styles.linkCell}>{o.sage_order_no || '\u2014'}</td>
                    <td title={o.po_number || undefined} style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.po_number || '\u2014'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatMoney(o.total || o.subtotal)}</td>
                    <td style={{ textAlign: 'right' }}>{o.line_item_count}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(docCounts[o.id] ?? 0) > 0 ? (
                        <span style={{
                          display: 'inline-block',
                          background: '#eff6ff',
                          color: '#2563eb',
                          borderRadius: '10px',
                          padding: '1px 8px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}>
                          {docCounts[o.id]}
                        </span>
                      ) : (
                        <span style={{ color: '#d1d5db' }}>{'\u2014'}</span>
                      )}
                    </td>
                    <td>{formatDate(o.created_at)}</td>
                  </tr>
                  {expandedTimeline === o.id && (
                    <tr>
                      <td colSpan={9} className={styles.timelineExpandRow}>
                        <OrderTimelineTab orderId={o.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {profile.bols && profile.bols.length > 0 && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Bills of Lading ({profile.bols_total})</div>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Status</th>
                <th>Sage Order #</th>
                <th>Linked</th>
                <th>Modified</th>
                <th style={{ textAlign: 'right' }}>Size</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {profile.bols.map((bol) => (
                <tr key={bol.id}>
                  <td title={bol.filename} style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {bol.display_name || bol.filename}
                  </td>
                  <td>
                    <span className={`${styles.orderBadge} ${styles[`order_${bol.status}`] || ''}`}>
                      {bol.status}
                    </span>
                  </td>
                  <td>{bol.sage_order_no || '\u2014'}</td>
                  <td>{bol.order_id ? '\u2705' : '\u2014'}</td>
                  <td>{formatDate(bol.drive_modified_time)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {bol.file_size_bytes != null ? formatFileSize(bol.file_size_bytes) : '\u2014'}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {/* Bearer-authenticated proxy (served from our GCS copy);
                        a bare href would 401 and the Drive viewer needs
                        Drive access — so both actions fetch a blob. */}
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => {
                        void bolReviewService.openPdf(bol.id).catch(() =>
                          window.alert('Could not open the BOL. Please try again.'),
                        );
                      }}
                    >
                      View
                    </button>
                    {' · '}
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => {
                        void bolReviewService
                          .downloadPdf(bol.id, bol.display_name || bol.filename)
                          .catch(() => window.alert('Could not download the BOL. Please try again.'));
                      }}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {profile.sage_import_orders && profile.sage_import_orders.length > 0 && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Sage Import Orders ({profile.sage_import_orders_total})</div>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Sage #</th>
                <th>PO #</th>
                <th>Status</th>
                <th>Ship Date</th>
                <th style={{ textAlign: 'right' }}>Items</th>
                <th>Linked</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {profile.sage_import_orders.map((sio) => (
                <tr
                  key={sio.id}
                  style={sio.is_test ? { opacity: 0.5 } : undefined}
                  className={sio.linked_order_id ? styles.clickableRow : undefined}
                  onClick={sio.linked_order_id ? () => setSelectedOrderId(sio.linked_order_id!) : undefined}
                  title={sio.linked_order_id ? 'View linked order details' : undefined}
                >
                  <td style={{ fontWeight: 500 }}>{sio.sage_order_no || '\u2014'}</td>
                  <td>{sio.customer_po_no || '\u2014'}</td>
                  <td>
                    <span className={`${styles.orderBadge} ${styles[`order_${sio.processing_status}`] || ''}`}>
                      {sio.processing_status}
                    </span>
                  </td>
                  <td>{sio.udf_required_by ? formatDate(sio.udf_required_by) : '\u2014'}</td>
                  <td style={{ textAlign: 'right' }}>{sio.line_item_count}</td>
                  <td>{sio.linked_order_id ? '\u2705' : '\u2014'}</td>
                  <td>{formatDate(sio.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.cardTitle}>Recent Invoices ({profile.recent_invoices_total})</div>
        {profile.recent_invoices.length === 0 ? (
          <div className={styles.emptyState}>No invoice history</div>
        ) : (
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Item Code</th>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {profile.recent_invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.invoice_no || '\u2014'}</td>
                  <td>{formatDate(inv.invoice_date)}</td>
                  <td>{inv.item_code || '\u2014'}</td>
                  <td>{inv.item_description || '\u2014'}</td>
                  <td style={{ textAlign: 'right' }}>{inv.quantity_shipped || '\u2014'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatMoney(inv.extension_amt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <OrderDetailModal
        isOpen={!!selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        orderId={selectedOrderId}
        onOrderAction={() => {/* no-op: read-only context */}}
      />
    </>
  );
}

// =============================================================================
// Quotes Tab
// =============================================================================

function QuotesTab({
  profile,
  onCreateOrder,
}: {
  profile: Customer360Profile;
  /** "Create Order" on a quote row: open the create-order dialog for it. */
  onCreateOrder: (quote: CustomerQuoteItem) => void;
}) {
  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardTitle}>Quotes ({profile.quotes_total})</div>
        {profile.quotes.length === 0 ? (
          <div className={styles.emptyState}>No quotes found</div>
        ) : (
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Project</th>
                <th>Status</th>
                <th>Created</th>
                <th>Expires</th>
                <th style={{ textAlign: 'right' }}>Products</th>
                <th style={{ textAlign: 'right' }}>Est. Total</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {profile.quotes.map((q) => (
                <tr key={q.id}>
                  <td>{q.source_id}</td>
                  <td>{q.project_name || '\u2014'}</td>
                  <td>
                    <span className={styles.orderBadge}>{q.status || 'unknown'}</span>
                  </td>
                  <td>{formatDate(q.created_date)}</td>
                  <td>{formatDate(q.expiration_date)}</td>
                  <td style={{ textAlign: 'right' }}>{q.product_count}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {q.estimated_total != null ? formatMoney(q.estimated_total) : '\u2014'}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className={styles.rowActionBtn}
                      onClick={() => onCreateOrder(q)}
                      data-action="quote_create_order_open"
                      data-target-quote-id={q.source_id}
                      data-testid={`quote-create-order-${q.source_id}`}
                      title={`Create a draft order linked to quote #${q.source_id}: its line items and prices are copied in and UDF_QUOTE is set for Sage`}
                    >
                      Create Order
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.cardTitle}>Special Pricing Agreements ({profile.special_pricing.length})</div>
        {profile.special_pricing.length === 0 ? (
          <div className={styles.emptyState}>No active special pricing agreements</div>
        ) : (
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Held By</th>
                <th>Status</th>
                <th>Effective</th>
                <th>Expires</th>
                <th>Discount</th>
                <th style={{ textAlign: 'right' }}>Products</th>
              </tr>
            </thead>
            <tbody>
              {profile.special_pricing.map((sp) => (
                <tr key={sp.id}>
                  <td>{sp.source_id}</td>
                  <td>
                    {sp.source_customer_no || '\u2014'}
                    {sp.via_linked_customer && <LinkedBadge />}
                  </td>
                  <td>
                    {(() => {
                      const expired = !!sp.is_active && sp.in_force === false;
                      const bg = !sp.is_active ? '#f3f4f6' : expired ? '#fef3c7' : '#dcfce7';
                      const fg = !sp.is_active ? '#6b7280' : expired ? '#92400e' : '#16a34a';
                      return (
                        <span
                          className={styles.orderBadge}
                          style={{ backgroundColor: bg, color: fg }}
                          data-testid="special-pricing-status"
                          title={expired ? 'Still flagged active in the Sales App, but the end date has passed. Its prices no longer apply to orders.' : undefined}
                        >
                          {!sp.is_active ? 'Inactive' : expired ? 'Expired' : 'Active'}
                        </span>
                      );
                    })()}
                  </td>
                  <td>{formatDate(sp.effective_date)}</td>
                  <td>{formatDate(sp.expiration_date)}</td>
                  <td>{sp.base_discount != null ? `${sp.base_discount}%` : '\u2014'}</td>
                  <td style={{ textAlign: 'right' }}>{sp.product_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.cardTitle}>
          Active Special Prices ({profile.special_pricing_products_total})
        </div>
        {profile.special_pricing_products.length === 0 ? (
          <div className={styles.emptyState}>No active special prices</div>
        ) : (
          <div style={{ maxHeight: 480, overflowY: 'auto' }}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Item Code</th>
                  <th>Product</th>
                  <th style={{ textAlign: 'right' }}>Price</th>
                  <th>Note</th>
                  <th>Held By</th>
                </tr>
              </thead>
              <tbody>
                {profile.special_pricing_products.map((p, i) => (
                  <tr key={`${p.source_customer_no}-${p.item_code}-${i}`}>
                    <td style={{ fontWeight: 500 }}>{p.item_code || '\u2014'}</td>
                    <td>{p.item_description || '\u2014'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>
                      {formatSpecialPrice(p)}
                    </td>
                    <td>{p.note || '\u2014'}</td>
                    <td>
                      {p.source_customer_no || '\u2014'}
                      {p.via_linked_customer && <LinkedBadge />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/** Badge marking pricing inherited from a linked-sibling customer. */
function LinkedBadge() {
  return (
    <span
      className={styles.orderBadge}
      style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', marginLeft: 6 }}
      title="Inherited from a linked customer (shared pricing group)"
    >
      linked
    </span>
  );
}

/**
 * Flat price, or the tier ladder for tiered special pricing.
 *
 * Tiered is checked FIRST, matching the order pipeline's own precedence
 * (`services/orders/price_resolution.py` resolves tiers before the flat
 * `special_price`, falling back only when no tier price exists). Reading
 * `special_price` first showed a number we never charge: HATCH01 / F1435.05
 * carries special_price 122.798 on a tiered row whose real rungs are
 * 134.95 / 124.95 (36+) / 116.95 (72+), so the price list disagreed with
 * both the sales site and the order the CSR would actually ship.
 */
function formatSpecialPrice(p: CustomerSpecialPricingProductItem): string {
  if ((p.pricing_type || '').toLowerCase() === 'tiered') {
    const ladder = formatTierLadder(p, formatMoney);
    if (ladder) return `Tiered: ${ladder}`;
  }
  if (p.special_price != null) return formatMoney(p.special_price);
  return '\u2014';
}

// =============================================================================
// Emails Tab
// =============================================================================

function EmailsTab({ profile }: { profile: Customer360Profile }) {
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>Email History ({profile.emails_total})</div>
      {profile.emails.length === 0 ? (
        <div className={styles.emptyState}>No emails routed to this customer</div>
      ) : (
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>Subject</th>
              <th>From</th>
              <th>Category</th>
              <th>Received</th>
            </tr>
          </thead>
          <tbody>
            {profile.emails.map((e) => (
              <tr key={e.id}>
                <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.subject || '(no subject)'}
                </td>
                <td>{e.from_address || '\u2014'}</td>
                <td>
                  {e.classification_category ? (
                    <span className={styles.orderBadge}>{e.classification_category}</span>
                  ) : '\u2014'}
                </td>
                <td>{formatDateTime(e.received_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// =============================================================================
// Locations Tab
// =============================================================================

function LocationsTab({ profile }: { profile: Customer360Profile }) {
  const { contact_info, salesperson } = profile;

  return (
    <>
      {/* Primary Contact */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>Primary Contact</div>
        <div className={styles.dataGrid}>
          <DataField label="Company" value={contact_info.customer_name} />
          <DataField label="Phone" value={contact_info.telephone_no} />
          <DataField label="Billing Email" value={contact_info.email_address} />
          {contact_info.cp_email_list_shipping && (
            <DataField label="Order Emails" value={contact_info.cp_email_list_shipping} />
          )}
          <DataField label="Fax" value={contact_info.fax_no} />
          <DataField label="Billing Address" value={[contact_info.address_line1, contact_info.city, contact_info.state, contact_info.zip_code].filter(Boolean).join(', ')} />
          {salesperson && (
            <>
              <DataField label="Sales Rep (Sage)" value={salesperson.salesperson_name} />
              <DataField label="Regional Responsible" value={salesperson.regional_responsible_name} />
              <DataField label="Sales Rep Email" value={salesperson.email_address} />
              <DataField label="Sales Rep Phone" value={salesperson.telephone_no} />
            </>
          )}
        </div>
      </div>

      {/* Sales Contacts grouped by location */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>
          Contacts
          <span className={styles.cardCount}>{profile.sales_contacts.length}</span>
          <span className={styles.sourceTag}>Sales App</span>
        </div>
        {profile.sales_contacts.length === 0 ? (
          <div className={styles.emptyState}>No contacts on file</div>
        ) : (
          <ContactsByLocation contacts={profile.sales_contacts} locations={profile.ship_locations} />
        )}
      </div>

      {/* Sage Ship-To Addresses */}
      <div className={styles.card}>
        <div className={styles.cardTitle}>
          Sage Ship-To Addresses
          <span className={styles.cardCount}>{profile.sage_ship_to_addresses.length}</span>
          <span className={styles.sourceTag} style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>Sage ERP</span>
        </div>
        {profile.sage_ship_to_addresses.length === 0 ? (
          <div className={styles.emptyState}>No Sage ship-to addresses on file</div>
        ) : (
          <div className={styles.locationGrid}>
            {profile.sage_ship_to_addresses.map((addr) => (
              <div
                key={addr.id}
                className={`${styles.locationCard} ${addr.udf_inactive === 'Y' ? styles.locationCardInactive : ''}`}
              >
                <div className={styles.locationTitle}>
                  {addr.ship_to_code || '—'}
                  {addr.ship_to_name && <span style={{ fontWeight: 400, marginLeft: 6 }}>— {addr.ship_to_name}</span>}
                  {addr.udf_inactive === 'Y' && (
                    <span className={styles.orderBadge} style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>Inactive</span>
                  )}
                </div>
                <div className={styles.locationDetail}>
                  {addr.ship_to_address1 && <div>{addr.ship_to_address1}</div>}
                  {addr.ship_to_address2 && <div>{addr.ship_to_address2}</div>}
                  {addr.ship_to_address3 && <div>{addr.ship_to_address3}</div>}
                  <div>{[addr.ship_to_city, addr.ship_to_state, addr.ship_to_zip_code].filter(Boolean).join(', ')}</div>
                  {addr.telephone_no && <div>Phone: {addr.telephone_no}{addr.telephone_ext ? ` x${addr.telephone_ext}` : ''}</div>}
                  {addr.fax_no && <div>Fax: {addr.fax_no}</div>}
                  {addr.email_address && <div>Email: {addr.email_address}</div>}
                  {addr.contact_code && <div>Contact: {addr.contact_code}</div>}
                  {(addr.warehouse_code || addr.ship_via || addr.salesperson_no) && (
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #e5e7eb', fontSize: '0.75rem', color: '#9ca3af' }}>
                      {addr.warehouse_code && <span>Warehouse: {addr.warehouse_code} </span>}
                      {addr.ship_via && <span>Ship Via: {addr.ship_via} </span>}
                      {addr.salesperson_no && <span>Rep: {addr.salesperson_no}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sales App Ship-To Locations */}
      {profile.ship_locations.length > 0 && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            Sales App Locations
            <span className={styles.cardCount}>{profile.ship_locations.length}</span>
            <span className={styles.sourceTag}>Sales App</span>
          </div>
          <div className={styles.locationGrid}>
            {profile.ship_locations.map((loc) => (
              <div
                key={loc.id}
                className={`${styles.locationCard} ${loc.is_primary ? styles.locationCardPrimary : ''}`}
              >
                <div className={styles.locationTitle}>
                  {loc.customer_location_no || 'Location'}
                  {loc.is_primary && <span className={styles.orderBadge} style={{ backgroundColor: '#dbeafe', color: '#2563eb' }}>Primary</span>}
                </div>
                <div className={styles.locationDetail}>
                  {loc.address_line1 && <div>{loc.address_line1}</div>}
                  {loc.address_line2 && <div>{loc.address_line2}</div>}
                  <div>{[loc.city, loc.state, loc.zip_code].filter(Boolean).join(', ')}</div>
                  {loc.telephone_no && <div>Phone: {loc.telephone_no}</div>}
                  {loc.email_address && <div>Email: {loc.email_address}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// =============================================================================
// CRM Tab
// =============================================================================

function CrmTab({ profile }: { profile: Customer360Profile }) {
  return (
    <>
      {profile.sage_order_notes && (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Order Notes</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
            Synced from Sage (AR Customer order notes)
          </div>
          <div className={styles.noteText} style={{ whiteSpace: 'pre-wrap' }}>
            {profile.sage_order_notes}
          </div>
        </div>
      )}
      <div className={styles.twoCol}>
      <div>
        <div className={styles.card}>
          <div className={styles.cardTitle}>Personal Notes ({profile.notes.length})</div>
          {profile.notes.length === 0 ? (
            <div className={styles.emptyState}>No notes recorded</div>
          ) : (
            profile.notes.map((n) => (
              <div key={n.id} className={styles.noteCard}>
                <div className={styles.noteHeader}>
                  <div>
                    <div className={styles.noteContact}>{n.contact_name}</div>
                    {n.role && <div className={styles.noteRole}>{n.role}</div>}
                  </div>
                  <div className={styles.noteDate}>{formatDateTime(n.created_at)}</div>
                </div>
                <div className={styles.noteText}>{n.note}</div>
              </div>
            ))
          )}
        </div>
      </div>

      <div>
        <div className={styles.card}>
          <div className={styles.cardTitle}>Account Updates ({profile.account_updates.length})</div>
          {profile.account_updates.length === 0 ? (
            <div className={styles.emptyState}>No account updates</div>
          ) : (
            profile.account_updates.map((u) => (
              <div key={u.id} className={styles.noteCard}>
                <div className={styles.noteHeader}>
                  <div className={styles.noteContact}>{u.source || 'Update'}</div>
                  <div className={styles.noteDate}>{formatDateTime(u.created_at)}</div>
                </div>
                <div className={styles.noteText}>{u.text}</div>
              </div>
            ))
          )}
        </div>
      </div>
      </div>
    </>
  );
}

// =============================================================================
// Financials Tab
// =============================================================================

function FinancialsTab({ profile }: { profile: Customer360Profile }) {
  const { ar_aging, sales_metrics, category_breakdown } = profile;

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardTitle}>Accounts Receivable Aging</div>
        <div className={styles.agingGrid}>
          <div className={styles.agingCard}><div className={styles.agingLabel}>Total Owed</div><div className={styles.agingValue} style={{ fontWeight: 700 }}>{formatMoney(totalOwed(ar_aging))}</div></div>
          <div className={styles.agingCard}><div className={styles.agingLabel}>0-30 Days</div><div className={styles.agingValue}>{formatMoney(ar_aging.current_balance)}</div></div>
          <div className={styles.agingCard}><div className={styles.agingLabel}>31-60 Days</div><div className={styles.agingValue}>{formatMoney(ar_aging.aging_category1)}</div></div>
          <div className={styles.agingCard}><div className={styles.agingLabel}>61-90 Days</div><div className={styles.agingValue}>{formatMoney(ar_aging.aging_category2)}</div></div>
          <div className={styles.agingCard}><div className={styles.agingLabel}>91-120 Days</div><div className={styles.agingValue}>{formatMoney(ar_aging.aging_category3)}</div></div>
          <div className={styles.agingCard}><div className={styles.agingLabel}>120+ Days</div><div className={styles.agingValue}>{formatMoney(ar_aging.aging_category4)}</div></div>
          <div className={styles.agingCard}><div className={styles.agingLabel}>Open Orders</div><div className={styles.agingValue}>{formatMoney(ar_aging.open_order_amt)}</div></div>
        </div>
      </div>

      <div className={styles.twoCol}>
        <div className={styles.card}>
          <div className={styles.cardTitle}>Payment Metrics</div>
          <div className={styles.dataGrid}>
            <DataField label="Avg Days to Pay" value={ar_aging.avg_days_payment_invoice ? `${ar_aging.avg_days_payment_invoice} days` : '\u2014'} />
            <DataField label="Avg Days Overdue" value={ar_aging.avg_days_over_due ? `${ar_aging.avg_days_over_due} days` : '\u2014'} />
            <DataField label="Last Payment" value={formatMoney(ar_aging.last_payment_amt)} />
            <DataField label="Highest Balance" value={formatMoney(ar_aging.highest_stmnt_balance)} />
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>Sales Summary</div>
          <div className={styles.dataGrid}>
            <DataField label="YTD Sales" value={formatMoney(sales_metrics.ytd_sales)} />
            <DataField label="LYTD Sales" value={formatMoney(sales_metrics.lytd_sales)} />
            <DataField label="Last Year Total" value={formatMoney(sales_metrics.ly_total_sales)} />
            <DataField
              label="YTD vs LYTD"
              value={sales_metrics.ytd_vs_lytd_pct != null ? `${sales_metrics.ytd_vs_lytd_pct >= 0 ? '+' : ''}${sales_metrics.ytd_vs_lytd_pct.toFixed(1)}%` : '\u2014'}
            />
            <DataField label="Last Order" value={formatDate(sales_metrics.last_order_date)} />
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardTitle}>Product Category Breakdown</div>
        {category_breakdown.length === 0 ? (
          <div className={styles.emptyState}>No category data available</div>
        ) : (
          category_breakdown.map((cat, i) => (
            <div key={cat.category} className={styles.categoryBar}>
              <span className={styles.categoryName}>{cat.category}</span>
              <div className={styles.categoryBarTrack}>
                <div
                  className={styles.categoryBarFill}
                  style={{
                    width: `${cat.percentage}%`,
                    backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                  }}
                />
              </div>
              <span className={styles.categoryPct}>{cat.percentage}%</span>
              <span className={styles.categoryAmount}>{formatMoney(cat.amount)}</span>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// =============================================================================
// Contacts By Location
// =============================================================================

function ContactsByLocation({ contacts, locations }: { contacts: CustomerSalesContactItem[]; locations: CustomerShipLocationItem[] }) {
  const locationMap = new Map<number | null, CustomerSalesContactItem[]>();
  for (const c of contacts) {
    const key = c.location_id;
    if (!locationMap.has(key)) locationMap.set(key, []);
    locationMap.get(key)!.push(c);
  }

  const locationLookup = new Map(
    locations.map((loc) => {
      const locNo = loc.customer_location_no != null ? parseInt(loc.customer_location_no, 10) : null;
      return [locNo, loc];
    })
  );

  const sortedKeys = [...locationMap.keys()].sort((a, b) => {
    if (a == null) return 1;
    if (b == null) return -1;
    return a - b;
  });

  return (
    <div>
      {sortedKeys.map((locId) => {
        const group = locationMap.get(locId)!;
        const loc = locId != null ? locationLookup.get(locId) : null;
        const locLabel = loc
          ? `${loc.customer_location_no || 'Location'} — ${[loc.city, loc.state].filter(Boolean).join(', ') || loc.address_line1 || ''}`
          : locId != null
            ? `Location ${locId}`
            : 'Unassigned';

        return (
          <div key={locId ?? 'none'} style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb' }}>
              {locLabel}
              {loc?.is_primary && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#2563eb' }}>(Primary)</span>}
            </div>
            <div className={styles.locationGrid}>
              {group.map((c) => (
                <div key={c.id} className={styles.locationCard}>
                  <div className={styles.locationTitle}>{c.name}</div>
                  <div className={styles.locationDetail}>
                    {c.phone && <div>Phone: {c.phone}</div>}
                    {c.email && <div>Email: {c.email}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Shared Components
// =============================================================================

function DataField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className={styles.dataField}>
      <span className={styles.dataLabel}>{label}</span>
      <span className={styles.dataValue}>{value || '\u2014'}</span>
    </div>
  );
}
